import React from 'react';
import { Link } from 'react-router-dom';
import Avatar from '../Avatar';
import Button from '../Button';
import Icon from '../Icon';
import { Hidden } from 'src/components/Table';
import Tenant from '../Tenant';
import Checkbox from '../Checkbox';
import CategoryPill from '../CategoryPill/CategoryPill';
import { getRoleDisplayLabel } from 'src/common/roleRegistry/roleRegistryHelpers';
import {
  serviceRequestTableLocalTime,
  onRequestPhoneCall,
} from 'src/helpers/validationHelper';
import { sortByDate } from 'src/components/Table/sorting';
import { IContact } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import { ITableColumn } from 'src/components/Table/types';
import { IProperty } from 'src/pages/Reporting/ReportingFormTypes';

interface CreateContactsTableColumnsParams {
  selectedContacts: IContact[];
  onSelectContact: (userId: any) => void;
  handleEditContact: (data: any) => void;
  onDeleteContact: (data: any) => void;
  PATHS: any;
  ICONS: any;
  updateSelectedContactPropertyDetails: (contactToSelect: IContact) => void;
  allSelected: any;
  onSelectAllContacts: any;
  properties: IProperty[] | null;
  propertiesError: string | null;
  onEditRole: (contact: any) => void;
  onEditEmail: (contact: any) => void;
  canEditContactEmail: boolean;
}

const sortByString = (a: string, b: string): number => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.toLowerCase().localeCompare(b.toLowerCase());
};

export const createContactsTableColumns = ({
  selectedContacts,
  onSelectContact,
  handleEditContact,
  onDeleteContact,
  PATHS,
  ICONS,
  updateSelectedContactPropertyDetails,
  allSelected,
  onSelectAllContacts,
  properties,
  propertiesError,
  onEditRole,
  onEditEmail,
  canEditContactEmail,
}: CreateContactsTableColumnsParams): ITableColumn[] => {
  return [
    {
      label: (
        <Checkbox readOnly value={allSelected} onChange={onSelectAllContacts} />
      ),
      width: '5rem',
      cell: (data: any) => {
        const selectedValue: IContact = selectedContacts.filter(
          (sc: any) => sc.id === data.id
        )[0];

        const role = data?.roles?.[0]?.role?.name || '';

        if (role !== 'Vendor' && data.status !== 'Active') {
          return (
            <Checkbox
              value={!!(selectedValue && selectedValue.id) || allSelected}
              onChange={() => onSelectContact(data.id)}
            />
          );
        }
        return null;
      },
    },
    {
      label: 'Contact',
      sortable: true,
      accessor: 'firstname',
      width: '25rem',
      cell: (data: any) => {
        const type = data?.roles?.[0]?.role?.name || '';

        return (
          <Link
            to={{
              pathname: PATHS.CONTACTS_VIEW.replace(':id', data.id),
              state: { data: data },
            }}
          >
            <Tenant
              headline={`${data.firstname} ${data.lastname}`}
              avatar={
                <Avatar
                  badge={ICONS[type]}
                  url={data.profileImage}
                  size={undefined}
                />
              }
            />
          </Link>
        );
      },
    },
    {
      label: 'Role',
      sortable: true,
      accessor: 'roles[0].role.name',
      minWidth: '15rem',
      sortingAlgorithm: sortByString,
      cell: (data: any) => {
        if (!data?.roles?.length) {
          return null;
        }

        const displayRole: string = getRoleDisplayLabel(data.roles[0].role.name);

        return (
          <CategoryPill
            text={displayRole}
            trailingSlot={
              <span
                className="contacts-table-role-edit juniper-datatable-hover-action"
                title="Change role"
                onClick={(e: any) => {
                  e.stopPropagation();
                  onEditRole(data);
                }}
              >
                <Icon glyph="pencil" weight="light" size="small" />
              </span>
            }
          />
        );
      },
    },
    {
      label: 'Phone',
      sortable: true,
      accessor: 'phone',
      xsHide: true,
      width: '10rem',
      cell: (data: any) => onRequestPhoneCall(data.phone),
    },
    {
      sortable: true,
      xsHide: true,
      label: 'Email',
      accessor: 'email',
      width: '22rem',
      sortingAlgorithm: sortByString,
      cell: (data: any) => {
        const email: string = data?.email ?? '';

        if (!canEditContactEmail || data?.isSpark) {
          return <span title={email}>{email}</span>;
        }

        return (
          <span className="contacts-table-email-cell">
            <span className="contacts-table-email-text" title={email}>
              {email}
            </span>
            <span
              className="contacts-table-email-edit juniper-datatable-hover-action"
              title="Change email"
              onClick={(e: any) => {
                e.stopPropagation();
                onEditEmail(data);
              }}
            >
              <Icon glyph="pencil" weight="light" size="small" />
            </span>
          </span>
        );
      },
    },
    // Conditionally include Property Details column if there are properties OR if there's an error fetching properties from the API.
    ...((properties && properties.length > 0) || propertiesError
      ? [
          {
            xsHide: true,
            label: 'Property Details',

            width: '14rem',
            textOverflow: true,
            cell: (data: any) => {
              // If there's an error, display it in red.
              if (propertiesError) {
                return (
                  <span
                    style={{
                      color: '#dc3545',
                      fontSize: '0.875rem',
                    }}
                    title={propertiesError}
                  >
                    Error loading properties
                  </span>
                );
              }

              // Normal case - show "View Details" link.
              return (
                <span
                  style={{
                    color: '#36454F',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                  onMouseEnter={(e: any) => {
                    e.target.style.color = '#2a3a47';
                  }}
                  onMouseLeave={(e: any) => {
                    e.target.style.color = '#36454F';
                  }}
                  onClick={() => updateSelectedContactPropertyDetails(data)}
                >
                  View Details
                </span>
              );
            },
          },
        ]
      : []),
    {
      xsHide: true,
      sortable: true,
      sortingAlgorithm: sortByDate,
      label: 'Invited On',
      width: '10rem',
      accessor: 'invitedOn',
      cell: (data: any) => (
        <div>
          {data.invitedOn
            ? serviceRequestTableLocalTime(data.invitedOn)
            : 'N/A'}
        </div>
      ),
    },
    {
      xsHide: true,
      sortable: true,
      label: 'Status',
      width: '8rem',
      accessor: 'status',
      sortingAlgorithm: sortByString,
      cell: (data: any) => (
        <div>
          <div title={data.status}>{data.status}</div>
        </div>
      ),
    },
    {
      xsHide: true,
      width: '7rem',
      cell: (data: any) => (
        <Hidden>
          <Button
            color="semantic"
            label={<Icon glyph="pencil" weight="light" />}
            onClick={() => handleEditContact(data)}
          />
          <Button
            color="semantic"
            onClick={() => onDeleteContact(data)}
            label={<Icon glyph="trash" weight="light" />}
            style={{ marginLeft: '0.5rem' }}
          />
        </Hidden>
      ),
    },
    {
      xsHide: true,
      sortable: true,
      label: 'MFA',
      width: '8rem',
      accessor: 'mfaEnabled',
      sortingAlgorithm: (a: boolean, b: boolean): number => {
        if (a === b) return 0;
        return a ? -1 : 1;
      },
      cell: (data: any) => (
        <div>
          <div title={data.mfaEnabled ? 'Enabled' : 'Disabled'}>
            {data.mfaEnabled ? 'Enabled' : 'Disabled'}
          </div>
        </div>
      ),
    }
  ];
};
